"use client";

import React, { useEffect, useRef, useState, useMemo } from "react";
import { CoursesBrowser } from "./CoursesBrowser";
import { CoursesNotesBrowser, CoursesNotesBrowserNew } from "./CourseNotesBrowser";
import { ContentLayout } from "./ContentLayout";
import { EditorToolbar } from "./Toolbar";
import { useNotes } from "../contexts/NotesContext";
import { Note } from "./Note";
import { motion, AnimatePresence } from "framer-motion";

export const Notes = () => {
	const options = {};
	const [sidebarWidth, setSidebarWidth] = useState(() => options["filesai_width"] || 200);
	const [activeTab, setActiveTab] = useState<'navigation' | 'content'>('navigation');
	const [isSmallMode, setIsSmallMode] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const { noteId, notesMetadata, activeCourse, sidebarOpen, setSidebarOpen, transcriptTabOpen } = useNotes();

	const fileName = useMemo(() => {
		console.log(noteId, notesMetadata, notesMetadata?.[activeCourse]);
		const file = (notesMetadata?.[activeCourse] || []).filter((note: any) => note.id === noteId)?.[0];
		return file?.title || "No note selected";
	}, [noteId, activeCourse, notesMetadata]);

	// Track component width to determine layout mode
	useEffect(() => {
		const handleResize = () => {
			if (containerRef.current) {
				const width = containerRef.current.offsetWidth;
				setIsSmallMode(width < 600); // Switch to small mode below 600px
			}
		};

		handleResize(); // Initial check
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// Auto-switch to content tab when note is selected in small mode
	useEffect(() => {
		if (isSmallMode && noteId) {
			setActiveTab('content');
		}
	}, [noteId, isSmallMode]);

	// Small mode: 2-tab layout
	if (isSmallMode) {
		const renderNavigationTab = () => (
			<div className="flex flex-1 overflow-hidden font-normal font-theme">
				<div className="w-[45%] border-r border-theme">
					<CoursesBrowser />
				</div>
				<div className="w-[55%] min-h-0 flex flex-col gap-[10px] overflow-y-auto">
					<CoursesNotesBrowser />
				</div>
			</div>
		);

		const renderContentTab = () => (
			<div className="flex-1 flex flex-col font-normal font-theme">
				<EditorToolbar setActiveTab={() => setActiveTab("navigation")} />
				<div className="flex flex-1 h-[calc(100%-60px)] overflow-hidden">
					<ContentLayout />
					{/*sidebarOpen && (
						<div className="h-full" style={{ width: `${Math.min(sidebarWidth, 400)}px` }}>
							<LearnProvider context={{ "type": "note", "fileName": fileName, "fileId": noteId }} close={() => setSidebarOpen(false)}>
								{options["filesai_mode"] === "sidebar" && <LearnSidebar />}
								{options["filesai_mode"] === "floating" && <FloatingWindow />}
							</LearnProvider>
						</div>
					)*/}
				</div>
			</div>
		);

		return (
			<div ref={containerRef} className="flex h-full w-full bg-theme-0 text-theme-0 font-theme">
				{activeTab === 'navigation' ? renderNavigationTab() : renderContentTab()}
			</div>
		);
	}

	return (
		<div ref={containerRef} className="flex h-full w-full bg-theme-0 text-theme-0 font-theme font-normal">
			<div className="w-1/4 max-w-[225px] backdrop-filter backdrop-invert-[.02]">
				<CoursesBrowser />
			</div>
			<div className="w-full min-h-0 flex flex-col gap-[10px] backdrop-filter backdrop-invert-[.02] relative overflow-hidden ml-0">
				<div className="border-l border-theme overflow-hidden h-full">
					<AnimatePresence mode="wait" initial={false}>
						{noteId ? (
							<motion.div
								key="note"
								initial={{ opacity: .4 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: .4 }}
								transition={{ duration: 0.2, ease: "easeInOut" }}
								className="inset-0 h-full"
							>
								<Note />
							</motion.div>
						) : (
							<motion.div
								key="browser"
								initial={{ opacity: .4 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: .4 }}
								transition={{ duration: 0.2, ease: "easeInOut" }}
								className="inset-0 h-full"
							>
								<CoursesNotesBrowserNew />
							</motion.div>
						)}
						{noteId ? noteId : " no note id"}
					</AnimatePresence>
				</div>
			</div>
		</div>
	);

	/*
	// old version below
	// Full width mode: current side-by-side layout
	return (
		<div ref={containerRef} className="flex h-full w-full bg-theme-0 text-theme-0 font-theme font-normal">
			<div className="w-1/4 max-w-[260px] bg-theme-0 border-r border-theme">
				<CoursesBrowser />
			</div>
			<div className="w-1/3 max-w-[300px] border-r border-theme min-h-0 flex flex-col gap-[10px]">
				<CoursesNotesBrowser />
			</div>
			<div className="flex-1 flex flex-col">
				<EditorToolbar />
				<div className="flex flex-1 h-[calc(100%-60px)] overflow-hidden relative">
					<ContentLayout />
					{sidebarOpen && (
						<div className="h-full" style={{ width: `${Math.min(sidebarWidth, 400)}px` }}>
							<LearnProvider close={() => setSidebarOpen(false)} context={{ "type": "note", "fileName": fileName, "fileId": noteId }}>
								{options["filesai_mode"] === "sidebar" && <LearnSidebar />}
								{options["filesai_mode"] === "floating" && <FloatingWindow />}
							</LearnProvider>
						</div>
					)}
					{!sidebarOpen && (
						<button onClick={() => setSidebarOpen((prev: boolean) => !prev)} className="absolute bottom-4 right-4 bg-theme-1 p-2 rounded-lg cursor-pointer hover:scale-110 transition-all duration-200 z-10">
							<Sparkles className="size-5" />
						</button>
					)}
				</div>
			</div>
		</div>
	);
	*/
};

export default Notes;